import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  ArrowRight, 
  ChevronRight,
  ArrowUp, 
  CheckCircle,
  Settings,
  Code,
  Zap,
  Lightbulb,
  ArrowDown,
  Layers,
  Activity,
  Wrench,
  Users,
  LineChart,
  ChevronDown,
  X,
  Star,
  Sparkles
} from 'lucide-react';

// Layout
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';

// Common components
import Container from './components/common/Container';
import SectionHeading from './components/common/SectionHeading';
import Button from './components/common/Button';

// Sections
import Newsletter from './components/sections/Newsletter';
import CTABanner from './components/sections/CTABanner';

const GaloServicesPage = () => {
  // State management
  const [scrolled, setScrolled] = useState(false);
  
  // Refs for scroll animations
  const servicesRef = useRef(null);
  const expertiseRef = useRef(null);

  // Services data with consistent branding colors
  const services = [
    {
      id: 'software',
      title: 'Control System Programming',
      shortDescription: 'Reliable control software for industrial automation.',
      description: 'We create robust control software for manufacturing environments. Our clean, well-documented code ensures reliable performance and easy maintenance.',
      icon: <Code className="w-6 h-6" />,
      image: "/img/code.webp",
      benefits: [
        'Error-resistant failsafe systems',
        'Optimized performance algorithms',
        'Clear technical documentation',
        'Seamless system integration'
      ],
      applications: ['Machine Controls', 'Process Automation', 'Quality Systems', 'Integration Interfaces']
    },
    {
      id: 'design',
      title: 'Engineering & Implementation',
      shortDescription: 'Practical system design for production floors.',
      description: 'We turn concepts into working systems that solve real manufacturing challenges. Our team handles everything from technical specifications to hands-on implementation.',
      icon: <Wrench className="w-6 h-6" />,
      image: "/img/robot4.jpeg",
      benefits: [
        'Production-ready technical designs',
        'Practical engineering solutions',
        'Skilled factory implementation',
        'Comprehensive performance testing'
      ],
      applications: ['Technical Design', 'System Integration', 'Factory Implementation', 'Performance Validation']
    },
    {
      id: 'innovation',
      title: 'Production Optimization',
      shortDescription: 'Targeted improvements for better manufacturing.',
      description: 'We enhance manufacturing performance without major disruption. By targeting key bottlenecks, we deliver substantial efficiency gains through strategic improvements.',
      icon: <Lightbulb className="w-6 h-6" />,
      image: "/img/robot6.jpeg",
      benefits: [
        'Precision workflow analysis',
        'Strategic technology integration',
        'Minimal production disruption',
        'Measurable performance gains'
      ],
      applications: ['Workflow Enhancement', 'Capability Expansion', 'Efficiency Improvement', 'Technology Integration']
    },
    {
      id: 'automation',
      title: 'Manufacturing Systems',
      shortDescription: 'Reliable automation for complex production.',
      description: 'Our manufacturing solutions combine technical expertise with industry knowledge to tackle complex production challenges in demanding factory environments.',
      icon: <Settings className="w-6 h-6" />,
      image: "/img/robot7.jpeg",
      benefits: [
        'Factory-proven reliability',
        'Industry-specific expertise',
        'Production-focused design',
        'Quality control integration'
      ],
      applications: ['Production Automation', 'Assembly Systems', 'Process Control', 'Technical Integration']
    }
  ];

  // Core expertise - consistent with brand colors
  const coreExpertise = [
    {
      title: 'Technical Precision',
      description: 'Advanced expertise in control systems and programming for reliable industrial performance.',
      icon: <Settings className="w-5 h-5 sm:w-6 sm:h-6" />
    },
    {
      title: 'Practical Implementation',
      description: 'Real factory experience that translates concepts into working systems with minimal disruption.',
      icon: <Wrench className="w-5 h-5 sm:w-6 sm:h-6" />
    },
    {
      title: 'Operational Focus',
      description: 'Solutions that address real manufacturing constraints while maximizing efficiency.',
      icon: <Zap className="w-5 h-5 sm:w-6 sm:h-6" />
    },
    {
      title: 'Performance Metrics',
      description: 'Clear performance standards with measurable benchmarks to verify effectiveness.',
      icon: <LineChart className="w-5 h-5 sm:w-6 sm:h-6" />
    }
  ];

  // Handle scroll
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="font-sans antialiased overflow-x-hidden bg-white">
      {/* SEO Metadata */}
      <Helmet>
        <title>Manufacturing Engineering & Technical Automation | Galo Industries</title>
        <meta
          name="description"
          content="Galo Industries delivers specialized engineering expertise in control systems, technical integration, and process optimization for manufacturing environments."
        />
        <meta name="keywords" content="manufacturing engineering, control systems, technical integration, process optimization, industrial automation, production technology" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
      </Helmet>

      <Header current="services" />
      
      {/* Services Hero Section */}
      <section className="pt-16 sm:pt-20 lg:pt-24 pb-12 sm:pb-16 lg:pb-20 bg-gradient-to-br from-cyan-600 via-blue-700 to-blue-900 text-white relative overflow-hidden">
        {/* Enhanced Background Effects */}
        <div className="absolute inset-0 bg-[radial-gradient(circle,_rgba(255,255,255,0.1)_1px,_transparent_1px)] bg-[length:20px_20px] opacity-40"></div>
        <div className="absolute top-0 right-0 w-80 sm:w-96 h-80 sm:h-96 bg-cyan-400/15 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-80 sm:w-96 h-80 sm:h-96 bg-blue-400/15 rounded-full blur-3xl animate-pulse delay-1000"></div>
        
        <Container className="relative z-10">
          {/* Breadcrumb */}
          <div className="mb-4 sm:mb-6">
            <div className="flex items-center text-blue-100 text-xs sm:text-sm">
              <a href="/" className="hover:text-white transition-colors">Home</a>
              <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 mx-1 sm:mx-2 flex-shrink-0" />
              <span className="text-white font-medium flex items-center">
                <Sparkles className="w-4 h-4 mr-1" />
                Services
              </span>
            </div>
          </div>
          
          <div className="max-w-5xl mx-auto text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 sm:mb-8 leading-tight">
              Our <span className="relative">
                <span className="text-cyan-200">Services</span>
                <span className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-cyan-400 to-blue-300 rounded-full"></span>
              </span> and Solutions
            </h1>
            
            <p className="text-lg sm:text-xl md:text-2xl text-blue-100 mb-10 sm:mb-16 leading-relaxed px-4 max-w-4xl mx-auto">
              Specialized expertise in control systems, automation integration, and engineering solutions for complex manufacturing challenges.
            </p>
            
            <a 
              href="#our-services"
              className="inline-flex items-center bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 hover:shadow-2xl hover:shadow-cyan-500/25 text-white px-8 sm:px-10 py-4 rounded-xl transition-all duration-300 text-lg font-semibold transform hover:scale-105 shadow-lg"
            >
              <span className="mr-3">Explore Our Services</span>
              <ArrowDown className="w-5 h-5" />
            </a>
          </div>
        </Container>
      </section>
      
      {/* Services Overview Section */}
      <section id="our-services" className="py-20 sm:py-24 lg:py-32 bg-gradient-to-b from-white to-slate-50">
        <Container>
          <SectionHeading
            eyebrow="What We Do"
            title={<>Complete <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-700">Service Portfolio</span></>}
            description="From control system programming to full manufacturing implementation, we deliver comprehensive solutions for complex industrial challenges."
            centered
          />
          
          {/* Services Grid */}
          <div className="grid lg:grid-cols-2 gap-12 sm:gap-16 max-w-7xl mx-auto">
            {services.map((service, index) => (
              <div key={service.id} className="group">
                {/* Service Card */}
                <div className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 hover:border-cyan-200">
                  {/* Service Header */}
                  <div className="relative">
                    <div className="aspect-[16/9] overflow-hidden">
                      <img 
                        src={service.image || "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&h=400&fit=crop"}
                        alt={`${service.title} by Galo Industries`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-br from-cyan-600/20 to-blue-800/20 group-hover:from-cyan-600/30 group-hover:to-blue-800/30 transition-all duration-500"></div>
                    </div>
                    
                    {/* Floating Icon */}
                    <div className="absolute -bottom-6 left-8">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 text-white flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:from-cyan-600 group-hover:to-blue-700 transition-all duration-300">
                        {service.icon}
                      </div>
                    </div>
                  </div>
                  
                  {/* Service Content */}
                  <div className="p-8 sm:p-10 pt-12">
                    <div className="mb-8">
                      <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4 group-hover:text-cyan-700 transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-slate-600 text-lg leading-relaxed">
                        {service.description}
                      </p>
                    </div>
                    
                    {/* Benefits & Applications Grid */}
                    <div className="grid sm:grid-cols-2 gap-8">
                      {/* Benefits */}
                      <div>
                        <h4 className="text-lg font-bold text-slate-900 mb-4 flex items-center">
                          <CheckCircle className="w-5 h-5 text-cyan-600 mr-2" />
                          Key Benefits
                        </h4>
                        <div className="space-y-3">
                          {service.benefits.map((benefit, benefitIndex) => (
                            <div key={benefitIndex} className="flex items-start text-sm">
                              <div className="w-2 h-2 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 mt-2 mr-3 flex-shrink-0"></div>
                              <span className="text-slate-700">{benefit}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Applications */}
                      <div>
                        <h4 className="text-lg font-bold text-slate-900 mb-4 flex items-center">
                          <Layers className="w-5 h-5 text-cyan-600 mr-2" />
                          Applications
                        </h4>
                        <div className="space-y-3">
                          {service.applications.map((application, appIndex) => (
                            <div key={appIndex} className="flex items-start text-sm">
                              <div className="w-2 h-2 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 mt-2 mr-3 flex-shrink-0"></div>
                              <span className="text-slate-700">{application}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Services CTA */}
          <div className="text-center mt-16 sm:mt-20">
            <div className="bg-gradient-to-r from-cyan-600 to-blue-700 text-white rounded-3xl p-10 sm:p-16 shadow-2xl relative overflow-hidden">
              {/* Background effects */}
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 via-blue-500/10 to-blue-500/10 rounded-3xl"></div>
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-cyan-400/20 to-transparent rounded-full blur-2xl"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-blue-400/20 to-transparent rounded-full blur-2xl"></div>
              
              <div className="relative z-10">
                <h3 className="text-3xl sm:text-4xl font-bold mb-6">Ready to discuss your project?</h3>
                <p className="text-blue-100 mb-10 text-xl max-w-3xl mx-auto leading-relaxed">
                  Our engineering team specializes in practical automation solutions for complex production environments.
                </p>
                <Button 
                  variant="secondary"
                  to="/contact"
                  className="bg-blue-800 hover:bg-blue-900 text-white px-8 sm:px-10 py-4 rounded-xl font-bold text-lg hover:shadow-lg transition-all duration-300 inline-flex items-center transform hover:scale-105 border border-blue-600"
                >
                  Schedule a Consultation
                </Button>
              </div>
            </div>
          </div>
        </Container>
      </section>
      
      {/* Core Expertise Section */}
      <section className="py-20 sm:py-24 lg:py-32 bg-gradient-to-b from-slate-50 to-white" ref={expertiseRef}>
        <Container>
          <SectionHeading
            eyebrow="Our Approach"
            title={<>The <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-700">Engineering Expertise</span> That Defines Us</>}
            description="Our methodology combines specialized expertise with practical manufacturing knowledge to deliver exceptional results."
            centered
          />
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 max-w-7xl mx-auto">
            {coreExpertise.map((expertise, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-8 sm:p-10 shadow-xl border border-slate-100 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 group text-center hover:border-cyan-200"
              >
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-r from-cyan-500 to-blue-600 text-white flex items-center justify-center mb-8 mx-auto group-hover:scale-110 group-hover:from-cyan-600 group-hover:to-blue-700 transition-all duration-300 shadow-xl">
                  {expertise.icon}
                </div>
                
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-5 group-hover:text-cyan-700 transition-colors">
                  {expertise.title}
                </h3>
                
                <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
                  {expertise.description}
                </p>
              </div>
            ))}
          </div>
        </Container>
      </section>
      
      {/* Process Section */}
      <section className="py-20 sm:py-24 lg:py-32 bg-gradient-to-b from-white to-slate-50">
        <Container>
          <SectionHeading
            eyebrow="How We Work"
            title={<>Our <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-700">Proven Process</span></>}
            description="A systematic approach that ensures successful project delivery from concept to implementation."
            centered
          />
          
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8 sm:gap-12">
              <div className="text-center group">
                <div className="relative mb-8">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white flex items-center justify-center mx-auto text-2xl font-bold shadow-xl group-hover:scale-110 transition-all duration-300">
                    01
                  </div>
                  {/* Connection line */}
                  <div className="hidden md:block absolute top-10 left-full w-full h-0.5 bg-gradient-to-r from-cyan-200 to-blue-200 -translate-y-0.5"></div>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4">Analysis & Planning</h3>
                <p className="text-slate-600 leading-relaxed">
                  We thoroughly assess your manufacturing challenges and develop comprehensive solutions tailored to your specific needs.
                </p>
              </div>
              
              <div className="text-center group">
                <div className="relative mb-8">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white flex items-center justify-center mx-auto text-2xl font-bold shadow-xl group-hover:scale-110 transition-all duration-300">
                    02
                  </div>
                  {/* Connection line */}
                  <div className="hidden md:block absolute top-10 left-full w-full h-0.5 bg-gradient-to-r from-cyan-200 to-blue-200 -translate-y-0.5"></div>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4">Design & Development</h3>
                <p className="text-slate-600 leading-relaxed">
                  Our expert team creates detailed technical specifications and develops robust solutions using industry best practices.
                </p>
              </div>
              
              <div className="text-center group">
                <div className="relative mb-8">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white flex items-center justify-center mx-auto text-2xl font-bold shadow-xl group-hover:scale-110 transition-all duration-300">
                    03
                  </div>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4">Implementation & Support</h3>
                <p className="text-slate-600 leading-relaxed">
                  We deploy your solution with minimal disruption and provide ongoing support to ensure optimal performance.
                </p>
              </div>
            </div>
          </div>
        </Container>
      </section>
      
      {/* CTA Banner */}
      <CTABanner
        title="Technical challenges in your manufacturing operations?"
        body="Our engineering team specializes in practical automation solutions for complex production environments."
        primary={{ label: 'Schedule a Consultation', href: '/contact' }}
        secondary={{ label: 'Explore Our Experience', href: '/projects' }}
      />
      
      {/* Newsletter */}
      <Newsletter />
      
      {/* Footer */}
      <Footer />
      
      {/* Back to top button */}
      {scrolled && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-4 sm:bottom-6 right-4 sm:right-6 w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-full shadow-lg flex items-center justify-center hover:-translate-y-1 transition-all duration-300 z-50 hover:shadow-xl hover:from-cyan-600 hover:to-blue-700"
          aria-label="Back to top"
        >
          <ArrowUp className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>
      )}
    </div>
  );
};

export default GaloServicesPage;